import { Fragment, type ReactNode } from 'react'
import { MediaImage } from './MediaImage'

/**
 * Le rendu du dialecte de mise en forme — 04 §4, D-29, D-30.
 *
 * **Pourquoi pas une bibliothèque.** `react-markdown` pèse plus de 100 Ko une
 * fois ses dépendances comptées, et `marked` en fait 30 — pour un budget total
 * de 150 Ko (05 §8). Surtout, elles rendent *tout* Markdown, alors que notre
 * dialecte est **fermé** : gras, italique, trois balises, listes, citations,
 * code, tableaux, formules, images. Une bibliothèque générale nous ferait
 * accepter au rendu ce que le serveur refuse à l'écriture — liens, HTML libre,
 * titres — et la garantie disparaîtrait en silence.
 *
 * **Aucun `dangerouslySetInnerHTML`.** On produit des nœuds React. Le contenu
 * vient d'un agent d'extraction et d'éditeurs ; le sérialiser en HTML dans une
 * page où le candidat est authentifié n'aurait aucune raison d'être.
 */

/** Ce que le serveur admet, et rien d'autre (`RichText::BALISES`). */
const BALISES: Record<string, (contenu: ReactNode, cle: string) => ReactNode> = {
  // Markdown n'a aucune syntaxe de soulignement, et toute l'épreuve de
  // français en dépend : « le mot souligné est un… ».
  u: (contenu, cle) => <u key={cle}>{contenu}</u>,
  sub: (contenu, cle) => <sub key={cle}>{contenu}</sub>,
  sup: (contenu, cle) => <sup key={cle}>{contenu}</sup>,
}

/**
 * Découpe une ligne en nœuds : code, images, formules, balises, gras, italique.
 *
 * L'ordre des motifs compte. Le **code** passe en premier parce qu'il est
 * littéral : `` `<script>` `` cité dans un énoncé d'informatique n'est pas une
 * balise, et `` `$` `` n'est pas une formule.
 */
const MOTIF = new RegExp(
  [
    '`[^`]+`', // code littéral — toujours en premier
    '!\\[[^\\]]*\\]\\(media:[A-Za-z0-9_-]+\\)', // image (D-30)
    '\\$\\$[^$]+\\$\\$', // formule en bloc
    '\\$[^$]+\\$', // formule dans le fil
    '</?(?:u|sub|sup)>', // les trois balises admises
    // **Le gras-italique passe EN PREMIER** — [DECISIONS §229]. Sans lui,
    // un titre d'œuvre entouré de trois étoiles ne trouvait aucun motif :
    // après les deux premières, `[^*]+` refuse la troisième, et l'italique
    // refuse la deuxième. Le jeton n'était donc pas reconnu, et le dialecte
    // **rendait ses propres marqueurs au candidat**, dans le corps d'une
    // leçon publiée.
    '\\*\\*\\*[^*]+\\*\\*\\*', // gras et italique
    // **Le gras tolère UN italique à l'intérieur** — [DECISIONS §229].
    // `[^*]+` interdisait toute étoile entre les deux paires : « **Le texte de
    // *La Dessalinienne* est de Lhérisson.** » n'était donc jamais du gras, et
    // le motif de l'italique appariait l'étoile d'ouverture avec celle du titre
    // — toute la ligne se décalait. Mesuré : 587 étoiles servies en clair avec
    // `[^*]+`, 497 avec cette alternation.
    //
    // **On ne rend PAS l'italique tolérant en retour.** Mesuré aussi : le motif
    // devient gourmand sur un paragraphe recollé et le compte remonte à 1 106,
    // presque le double. Le correctif symétrique était le réflexe ; la mesure
    // dit le contraire.
    //
    // L'alternation est déterministe au premier caractère — `[^*]` d'un côté,
    // `*` de l'autre — donc pas de retour arrière catastrophique.
    '\\*\\*(?:[^*]|\\*[^*]+\\*)+\\*\\*', // gras
    '\\*[^*]+\\*', // italique
  ].join('|'),
  'g',
)

export interface RenduOptions {
  /**
   * F1.3 — l'économie de données. Les images ne se chargent qu'à la demande :
   * un schéma non voulu coûte au candidat de l'argent qu'il n'a pas.
   */
  economieDeDonnees?: boolean | undefined
  /**
   * Rendu des mathématiques, chargé à la demande (ADR-07).
   *
   * `undefined` est un état ORDINAIRE, pas un oubli : c'est celui d'une carte
   * sans formule, et celui d'une carte dont KaTeX n'est pas encore arrivé.
   */
  rendreMath?: ((latex: string, bloc: boolean) => ReactNode) | undefined
}

/** Le contenu d'une ligne, en nœuds React. */
export function ligneEnNoeuds(texte: string, options: RenduOptions = {}): ReactNode[] {
  const noeuds: ReactNode[] = []
  const pile: { nom: string; enfants: ReactNode[] }[] = []
  let curseur = 0
  let compteur = 0

  const pousser = (noeud: ReactNode): void => {
    const dessus = pile[pile.length - 1]

    if (dessus === undefined) noeuds.push(noeud)
    else dessus.enfants.push(noeud)
  }

  for (const trouve of texte.matchAll(MOTIF)) {
    const debut = trouve.index

    if (debut > curseur) pousser(texte.slice(curseur, debut))

    const jeton = trouve[0]
    const cle = `n${String(compteur++)}`
    curseur = debut + jeton.length

    // ── Ouverture / fermeture d'une balise ──────────────────────────────
    if (jeton.startsWith('</')) {
      const nom = jeton.slice(2, -1)
      const ouverte = pile[pile.length - 1]

      if (ouverte?.nom === nom) {
        pile.pop()
        pousser(BALISES[nom]?.(ouverte.enfants, cle) ?? ouverte.enfants)
      }
      continue
    }

    if (jeton.startsWith('<')) {
      pile.push({ nom: jeton.slice(1, -1), enfants: [] })
      continue
    }

    pousser(jetonEnNoeud(jeton, cle, options))
  }

  if (curseur < texte.length) pousser(texte.slice(curseur))

  // Une balise jamais refermée ne doit pas faire disparaître son contenu :
  // le serveur la refuse à l'écriture, mais un contenu chargé avant cette
  // règle existe peut-être encore.
  while (pile.length > 0) {
    const orpheline = pile.pop()

    if (orpheline !== undefined) noeuds.push(...orpheline.enfants)
  }

  return noeuds
}

function jetonEnNoeud(jeton: string, cle: string, options: RenduOptions): ReactNode {
  if (jeton.startsWith('`')) {
    return (
      <code
        key={cle}
        className="rounded px-1 font-mono text-[0.9em]"
        style={{ background: 'var(--surface-2)' }}
      >
        {jeton.slice(1, -1)}
      </code>
    )
  }

  if (jeton.startsWith('![')) {
    const alt = jeton.slice(2, jeton.indexOf(']'))
    const id = jeton.slice(jeton.indexOf('media:') + 6, -1)

    return <MediaImage key={cle} id={id} alt={alt} economieDeDonnees={options.economieDeDonnees} />
  }

  if (jeton.startsWith('$')) {
    const bloc = jeton.startsWith('$$')
    const latex = bloc ? jeton.slice(2, -2) : jeton.slice(1, -1)
    const rendu = options.rendreMath?.(latex, bloc)

    // La cle vit ICI : le rendu mathematique est fourni de l'exterieur et n'a
    // aucune raison de connaitre notre decoupage.
    return <Fragment key={cle}>{rendu ?? jeton}</Fragment>
  }

  // ── Gras et italique : leur contenu est du texte RICHE ──────────────────
  //
  // **Ils rendaient leur contenu littéralement** — [DECISIONS §153]. Une puce
  // d'aide-mémoire s'écrit `**$N_A = 6{,}022 \times 10^{23}$**`, et le motif du
  // gras l'attrape en entier : rien, entre les astérisques, n'est un
  // astérisque. La formule se retrouvait à l'intérieur d'un jeton terminal, et
  // le candidat lisait les dollars. Ce n'était pas un cas de bord : « À retenir
  // pour le concours » met sa formule en gras PARCE QUE c'est ce qu'il faut
  // retenir, et un tableau de synthèse met ses cases en gras pour la même
  // raison — les deux endroits exacts où le défaut se voyait.
  //
  // La récursion s'arrête : le contenu d'un jeton est strictement plus court
  // que le jeton. Et elle n'ouvre rien — c'est `ligneEnNoeuds`, donc le même
  // dialecte fermé, pas un second analyseur.
  if (jeton.startsWith('***')) {
    return (
      <strong key={cle}>
        <em>{ligneEnNoeuds(jeton.slice(3, -3), options)}</em>
      </strong>
    )
  }

  if (jeton.startsWith('**')) {
    return <strong key={cle}>{ligneEnNoeuds(jeton.slice(2, -2), options)}</strong>
  }

  return <em key={cle}>{ligneEnNoeuds(jeton.slice(1, -1), options)}</em>
}
